/**
 * IndexedDB `mushaf-en`, version 1 — §13.1, §13.2.
 *
 * WRITE-THROUGH, NOT WRITE-BEHIND. A note must survive closing the tab immediately
 * afterwards, and the volumes are far too small for batching to be worth the lost
 * durability.
 *
 * THREE STORES PLUS `kv`. The Arabic app has `srs` and `programs` as well, and is on
 * version 2 because it migrated a database that existed in the field. This database
 * name has never existed, so there is no history to carry: version 1 creates exactly
 * what this app uses, and the legacy-khatmah data migration is gone with it.
 */
import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import { DB_NAME, DB_VERSION, type Bookmark, type Note, type PageState } from '@mushaf/corpus'

interface MushafDB extends DBSchema {
  notes: { key: string; value: Note }
  bookmarks: { key: string; value: Bookmark }
  pages: { key: number; value: PageState }
  kv: { key: string; value: unknown }
}

export type StoreName = 'notes' | 'bookmarks' | 'pages' | 'kv'
export const ALL_STORES: StoreName[] = ['notes', 'bookmarks', 'pages', 'kv']

let dbPromise: Promise<IDBPDatabase<MushafDB>> | null = null

export function getDb(): Promise<IDBPDatabase<MushafDB>> {
  if (dbPromise) return dbPromise

  dbPromise = (async () => {
    /**
     * IndexedDB HAS NO DOWNGRADE — a user on a later version who reverts the app sees
     * the open fail. Accepted for a static SPA with no staged rollout (§13.2). The
     * `oldVersion` guard stays even with one version, because that is the shape a
     * second one has to slot into.
     */
    return await openDB<MushafDB>(DB_NAME, DB_VERSION, {
      upgrade(database, oldVersion) {
        if (oldVersion < 1) {
          database.createObjectStore('notes', { keyPath: 'verseKey' })
          database.createObjectStore('bookmarks', { keyPath: 'verseKey' })
          database.createObjectStore('pages', { keyPath: 'page' })
          database.createObjectStore('kv')
        }
      },
    })
  })()

  return dbPromise
}

// ---------------------------------------------------------------------------
// Snapshot / replace
// ---------------------------------------------------------------------------

export interface LibrarySnapshot {
  notes: Note[]
  bookmarks: Bookmark[]
  pages: PageState[]
}

export async function readSnapshot(): Promise<LibrarySnapshot> {
  const db = await getDb()
  const [notes, bookmarks, pages] = await Promise.all([
    db.getAll('notes'),
    db.getAll('bookmarks'),
    db.getAll('pages'),
  ])
  return { notes, bookmarks, pages }
}

/**
 * Import REPLACES EVERYTHING IN ONE TRANSACTION ACROSS ALL STORES (§13.3).
 *
 * A half-applied import leaving notes from one device and bookmarks from another is
 * worse than a clean failure.
 */
export async function replaceAll(snapshot: LibrarySnapshot): Promise<void> {
  const db = await getDb()
  const tx = db.transaction(ALL_STORES, 'readwrite')
  await Promise.all([
    tx.objectStore('notes').clear(),
    tx.objectStore('bookmarks').clear(),
    tx.objectStore('pages').clear(),
    tx.objectStore('kv').clear(),
  ])
  await Promise.all([
    ...snapshot.notes.map((n) => tx.objectStore('notes').put(n)),
    ...snapshot.bookmarks.map((b) => tx.objectStore('bookmarks').put(b)),
    ...snapshot.pages.map((p) => tx.objectStore('pages').put(p)),
  ])
  await tx.done
}

export async function put<T>(store: Exclude<StoreName, 'kv'>, value: T): Promise<void> {
  const db = await getDb()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  await db.put(store as any, value as any)
}

export async function remove(store: Exclude<StoreName, 'kv'>, key: string | number): Promise<void> {
  const db = await getDb()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  await db.delete(store as any, key as any)
}

export async function putKv(key: string, value: unknown): Promise<void> {
  const db = await getDb()
  await db.put('kv', value, key)
}
